<template>
  <div
    class="dayCard"
    :class="{ 'dayCard--today': isToday, 'dayCard--large': large, 'dayCard--clickable': !large }"
    @click="!large && $emit('select')"
  >
    <div class="dayCardHeader">
      <div class="dayCardHeaderTop">
        <div class="d-flex align-center" style="gap: 8px;">
          <span class="dayTitle">{{ label }}</span>
          <span v-if="isToday" class="todayBadge">Aujourd'hui</span>
        </div>
        <span class="dayKcal">{{ day.totals.kcal.toFixed(0) }} kcal</span>
      </div>
      <button
        class="sameDayToggle"
        :class="{ 'sameDayToggle--active': isSameLunchDinner }"
        @click.stop="$emit('toggle-same-day')"
      >
        <v-icon size="11">mdi-repeat</v-icon>
        Déjeuner = Dîner
      </button>
    </div>

    <div v-if="day.menu && Object.keys(day.menu).length" class="dayMeals" :class="{ 'dayMeals--large': large }">
      <template v-for="mealName in MEAL_ORDER.filter(m => day.menu[m] !== undefined)" :key="mealName">
        <div
          v-for="(recipe, i2) in day.menu[mealName]"
          :key="recipe.id || i2"
          class="mealRow"
          :class="{ 'mealRow--large': large }"
          @click.stop="$emit('open-recipe', recipe)"
        >
          <div class="mealRowIcon">
            <v-img
              v-if="recipe.image || recipe.imageUrl"
              :src="recipe.image || recipe.imageUrl"
              cover
              class="mealRowImg"
            >
              <template #placeholder>
                <v-icon size="18" style="opacity:0.3">mdi-image-outline</v-icon>
              </template>
            </v-img>
            <v-icon v-else size="20" :icon="mealIconFor(mealName)" style="opacity:0.4" />
          </div>
          <div class="mealRowContent">
            <p class="mealRowLabel mb-0">{{ mealLabel(mealName) }}</p>
            <p class="mealRowTitle mb-0">{{ recipe.title || 'Sans titre' }}</p>
            <span v-if="large" class="mealRowKcal mealRowKcal--large">{{ (recipe.totals?.kcal/recipe.servings ?? 0).toFixed(0) }} kcal</span>
          </div>
          <span v-if="!large" class="mealRowKcal">{{ (recipe.totals?.kcal/recipe.servings ?? 0).toFixed(0) }} kcal</span>
          <v-icon v-if="!large" size="16" style="opacity:0.35">mdi-chevron-right</v-icon>
        </div>
      </template>
      <p v-if="!Object.values(day.menu).some(m => m && m.length)" class="text-caption text-grey">
        Aucune recette disponible pour ce jour.
      </p>
    </div>

    <div class="dailyTotals">
      <v-tooltip location="top">
        <template #activator="{ props: tp }">
          <span v-bind="tp" class="macroChip" :class="day.totals.protein >= proteinNeeds ? 'macro-ok' : 'macro-warn'">
            P {{ day.totals.protein.toFixed(0) }}g
          </span>
        </template>
        Cible : {{ proteinNeeds.toFixed(0) }}g
      </v-tooltip>
      <v-tooltip location="top">
        <template #activator="{ props: tp }">
          <span v-bind="tp" class="macroChip" :class="day.totals.fat <= fatNeeds + 5 ? 'macro-ok' : 'macro-warn'">
            L {{ day.totals.fat.toFixed(0) }}g
          </span>
        </template>
        Cible : {{ fatNeeds.toFixed(0) }}g
      </v-tooltip>
      <v-tooltip location="top">
        <template #activator="{ props: tp }">
          <span v-bind="tp" class="macroChip" :class="Math.abs(day.totals.carbs - carbNeeds) <= 20 ? 'macro-ok' : 'macro-warn'">
            G {{ day.totals.carbs.toFixed(0) }}g
          </span>
        </template>
        Cible : {{ carbNeeds.toFixed(0) }}g
      </v-tooltip>
    </div>
  </div>
</template>

<script>
const MEAL_ORDER = ['breakfast', 'lunch', 'snack', 'dinner'];
const MEAL_ICONS = {
  breakfast: "mdi-coffee-outline",
  lunch: "mdi-bowl-mix-outline",
  snack: "mdi-fruit-cherries",
  dinner: "mdi-bowl-mix-outline",
};

export default {
  name: "DayMenuCard",
  props: {
    day: { type: Object, required: true },
    label: { type: String, required: true },
    isToday: { type: Boolean, default: false },
    isSameLunchDinner: { type: Boolean, default: false },
    large: { type: Boolean, default: false },
    proteinNeeds: { type: Number, required: true },
    fatNeeds: { type: Number, required: true },
    carbNeeds: { type: Number, required: true },
  },
  emits: ["select", "toggle-same-day", "open-recipe"],
  setup() {
    const mealIconFor = (mealName) => MEAL_ICONS[mealName] || "mdi-food-variant";
    const mealLabel = (mealName) => {
      return mealName === "breakfast" ? "Petit-déjeuner"
        : mealName === "lunch" ? "Déjeuner"
        : mealName === "dinner" ? "Dîner"
        : "Collation";
    };
    return { MEAL_ORDER, mealIconFor, mealLabel };
  },
};
</script>

<style scoped>
.dayCard {
  border-radius: 16px;
  background: rgba(var(--v-theme-secondary), 0.9);
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  padding: 1.1rem 1.1rem 1rem;
  min-width: 0;
}

.dayCard--clickable {
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.dayCard--clickable:hover {
  border-color: rgba(var(--v-theme-accent), 0.35);
  background: rgba(var(--v-theme-tertiary), 0.9);
}

.dayCard--today {
  border: 2px solid rgb(var(--v-theme-accent));
  background: rgba(var(--v-theme-tertiary), 0.9);
}

.dayCard--large {
  padding: 1.1rem 1.25rem 1rem;
}

.dayCardHeader {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 0.85rem;
}

.dayCardHeaderTop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.dayTitle {
  font-size: 1.05rem;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
}

.dayCard--large .dayTitle {
  font-size: 1.15rem;
}

.todayBadge {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(var(--v-theme-accent), 0.18);
  color: rgb(var(--v-theme-accent));
}

.sameDayToggle {
  display: flex;
  align-items: center;
  align-self: flex-start;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  font-family: inherit;
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid rgba(var(--v-theme-text), 0.14);
  background: transparent;
  color: rgba(var(--v-theme-text), 0.45);
  cursor: pointer;
  transition: all 0.15s;
}

.sameDayToggle--active {
  background: rgba(var(--v-theme-accent), 0.14);
  color: rgb(var(--v-theme-accent));
  border-color: rgba(var(--v-theme-accent), 0.35);
}

.dayKcal {
  font-size: 0.85rem;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
}

.dayMeals {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.dayMeals--large {
  flex-direction: row;
  gap: 0.65rem;
}

.mealRow {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0.55rem 0.55rem;
  border-radius: 12px;
  border: 1px solid rgba(var(--v-theme-text), 0.07);
  background: rgba(var(--v-theme-tertiary), 0.55);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.mealRow--large {
  flex: 1;
  flex-direction: row;
  align-items: stretch;
  gap: 10px;
  padding: 0.6rem;
  min-width: 0;
}

.mealRow:hover {
  background: rgba(var(--v-theme-tertiary), 0.9);
  border-color: rgba(var(--v-theme-text), 0.14);
}

.mealRowIcon {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(var(--v-theme-text), 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
}

.mealRow--large .mealRowIcon {
  width: 60px;
  align-self: stretch;
  height: auto;
  border-radius: 8px;
}

.mealRowImg {
  width: 100%;
  height: 100%;
}

.mealRowContent {
  flex: 1;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.mealRowLabel {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(var(--v-theme-text), 0.45);
}

.mealRowTitle {
  font-size: 0.92rem;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mealRow--large .mealRowTitle {
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.25;
}

.mealRowKcal {
  flex-shrink: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(var(--v-theme-text), 0.55);
}

.mealRowKcal--large {
  margin-top: 2px;
}

@media (max-width: 600px) {
  .dayMeals--large {
    flex-direction: column;
  }

  .mealRow--large .mealRowTitle {
    -webkit-line-clamp: 1;
  }
}

.macro-ok {
  color: #4caf50;
  font-weight: 600;
}

.macro-warn {
  color: #ff9800;
  font-weight: 600;
}

.dailyTotals {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 0.9rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(var(--v-theme-text), 0.08);
}

.macroChip {
  font-size: 0.78rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(var(--v-theme-text), 0.06);
  border: 1px solid rgba(var(--v-theme-text), 0.08);
}
</style>
